import { useState, useRef, useEffect } from 'react';

export function RevealImage({ src, thumbSrc, alt, className = '', eager = false }) {
  const [fullLoaded, setFullLoaded] = useState(false);
  const [thumbLoaded, setThumbLoaded] = useState(false);
  const [inView, setInView] = useState(eager);
  const ref = useRef(null);

  useEffect(() => {
    if (eager) return;
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { rootMargin: '600px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [eager]);

  useEffect(() => {
    if (eager && src) {
      const img = new Image();
      img.src = src;
    }
  }, [eager, src]);

  const hasThumb = thumbSrc && thumbSrc !== src;

  return (
    <div ref={ref} className={`relative overflow-hidden ${className}`}>
      {/* Solid placeholder background */}
      <div
        className={`absolute inset-0 bg-zinc-900 transition-opacity duration-300 ${
          (hasThumb ? thumbLoaded : fullLoaded) ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
      />
      {/* Blurred thumbnail for progressive loading */}
      {inView && hasThumb && (
        <img
          src={thumbSrc}
          alt=""
          loading="eager"
          onLoad={() => setThumbLoaded(true)}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-200 ${
            thumbLoaded && !fullLoaded ? 'opacity-100 blur-sm scale-105' : 'opacity-0'
          }`}
          decoding="async"
          aria-hidden="true"
        />
      )}
      {/* Full resolution image */}
      {inView && (
        <img
          src={src}
          alt={alt || ''}
          loading={eager ? 'eager' : 'lazy'}
          fetchPriority={eager ? 'high' : 'auto'}
          onLoad={() => setFullLoaded(true)}
          className={`w-full h-full object-cover transition-opacity duration-300 ease-out ${
            fullLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          decoding="async"
        />
      )}
    </div>
  );
}

export function preloadImage(src) {
  if (!src) return;
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = src;
  document.head.appendChild(link);
}
